import { supabase } from '../lib/supabaseClient';
import type { LocationSource, PdvRow } from '../lib/db-types';

/** Radio de geocerca por defecto (app_settings.pdv_radius_m / pdv.radius_m DEFAULT 300). */
export const DEFAULT_PDV_RADIUS_M = 300;

export interface PdvInput {
  code: string;
  name: string;
  chain: string | null;
  format: string | null;
  department: string | null;
  province: string | null;
  district: string | null;
  zone: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  /**
   * Geocerca propia de la tienda, en metros.
   * OPCIONAL a propósito: si la clave NO está en el objeto, el upsert no toca la columna
   * (el PDV existente conserva el radio que le pusieron a mano; el PDV nuevo toma el
   * DEFAULT 300 de la tabla). Nunca mandar `radius_m: 300` "por si acaso".
   */
  radius_m?: number;
  /**
   * Procedencia de la coordenada. OPCIONAL por la misma razón que `radius_m`: si la clave
   * no está, el upsert no la toca y un PDV ya verificado en terreno conserva su marca.
   * Solo se manda cuando la lat/lng viene de un enlace de mapa ('mapa'); la captura con
   * GPS en el PDV la pone el servidor dentro de la RPC `set_pdv_location` ('terreno').
   */
  location_source?: LocationSource;
  location_set_at?: string;
  active?: boolean;
}

export async function listPdv(query: string): Promise<PdvRow[]> {
  let q = supabase.from('pdv').select('*').order('code');
  const term = query.trim();
  if (term !== '') {
    const like = `%${term}%`;
    q = q.or(`code.ilike.${like},name.ilike.${like},district.ilike.${like},chain.ilike.${like},zone.ilike.${like}`);
  }
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as PdvRow[];
}

/**
 * Crea un PDV NUEVO. Si el código ya existe, NO lo toca: avisa cuál es. El formulario de «Nuevo PDV» y la importación
 * de programación usaban `upsertPdv`, y pegar un código existente reescribía esa tienda sin aviso: el 16-oct Plaza
 * Vea Comas (T10194, de Takis) quedó como «Economax Challapampa» (0307).
 */
export async function createPdv(row: PdvInput): Promise<PdvRow> {
  const { data, error } = await supabase.from('pdv').insert(row).select().single();
  if (error) {
    if (error.code === '23505') {
      const { data: ya } = await supabase.from('pdv').select('code, name').eq('code', row.code).maybeSingle();
      throw new Error(
        `Ya existe el punto de venta ${row.code}${ya ? ` — ${ya.name}` : ''}. Usa otro código, o edítalo desde la lista.`,
      );
    }
    throw new Error(error.message);
  }
  return data as PdvRow;
}

/** Edita un PDV que ya existe, por su id (no por el código: así cambiar el código tampoco pisa otra tienda). */
export async function updatePdv(id: string, row: PdvInput): Promise<PdvRow> {
  const { data, error } = await supabase.from('pdv').update(row).eq('id', id).select().single();
  if (error) {
    if (error.code === '23505') throw new Error(`El código ${row.code} ya es de otro punto de venta: usa otro.`);
    throw new Error(error.message);
  }
  return data as PdvRow;
}

/**
 * Upsert en lote por `code`. IMPORTANTE: PostgREST exige que TODAS las filas del mismo lote
 * tengan exactamente las mismas claves, así que quien llama debe separar las filas que traen
 * `radius_m` de las que no (ver `confirmImport` en `Pdv.tsx`).
 */
export async function bulkUpsertPdv(rows: PdvInput[]): Promise<number> {
  if (rows.length === 0) return 0;
  const { data, error } = await supabase
    .from('pdv')
    .upsert(rows, { onConflict: 'code' })
    .select('id');
  if (error) throw new Error(error.message);
  return (data ?? []).length;
}

/**
 * Fija la coordenada REAL de la tienda con el GPS de quien está parado en ella.
 * Va por RPC y no por UPDATE porque `pdv_update_admin` (0002_rls.sql) limita el UPDATE a
 * admin: la RPC `set_pdv_location` (0012) valida el rol adentro, marca la fila como
 * `location_source = 'terreno'` y deja el rastro en `audit_log`.
 */
export async function setPdvLocation(
  pdvId: string,
  lat: number,
  lng: number,
  accuracy: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_pdv_location', {
    p_pdv: pdvId,
    p_lat: lat,
    p_lng: lng,
    p_accuracy: accuracy,
  });
  if (!error) return;
  const m = error.message ?? '';
  if (m.includes('no_autorizado')) {
    throw new Error('Solo el coordinador o el jefe de operaciones puede fijar la ubicación de una tienda.');
  }
  if (m.includes('coordenada_invalida')) {
    throw new Error('La coordenada obtenida no es válida. Vuelve a tomar la ubicación.');
  }
  if (m.includes('pdv_inexistente')) {
    throw new Error('Esa tienda ya no existe.');
  }
  throw new Error(m || 'No se pudo guardar la ubicación de la tienda.');
}

export async function setPdvActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('pdv').update({ active }).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Radio actual de los PDV que ya existen, indexado por código.
 * Se consulta en bloques para no pasarse del largo de URL con archivos de cientos de filas.
 */
export async function getExistingPdvRadii(codes: string[]): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  const unique = Array.from(new Set(codes.filter((c) => c !== '')));
  const CHUNK = 100;
  for (let i = 0; i < unique.length; i += CHUNK) {
    const chunk = unique.slice(i, i + CHUNK);
    const { data, error } = await supabase.from('pdv').select('code, radius_m').in('code', chunk);
    if (error) throw new Error(error.message);
    for (const row of (data ?? []) as { code: string; radius_m: number }[]) {
      out.set(row.code, row.radius_m);
    }
  }
  return out;
}
